import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { hapticError, hapticTick } from '../../_utils/haptics';
import { seChoicesService } from '../../_services/seChoices.service';
import { marketingService } from '../../_services/marketing.service';
import { newServices } from '../../_services/newservices.service';
import { clientSharingService } from '../../_services/clientSharing.service';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';
import { Choices, Saved, Skeleton } from '../onboarding/setup/ui';
import {
  firstWord, loadTeam, myIds, ownerOf, reasonOf, saveProFlags, useAlive, useFlash, useSerial,
} from '../onboarding/setup/data/stylist';
import {
  ClientShareChoice, DiaryChoice, salonAccessLine, useClientShare, useDiaryChoice,
} from '../onboarding/setup/shared/PrivacyChoices';
import { DISCOUNTS_VISIBLE } from '../../config/featureFlags';
import { loadChoices, saveMyChoice, answerOf } from '../discounts/discountStore';
import { money, splitFor } from '../discounts/data/codes';
import '../onboarding/setup/setup.css';
import '../onboarding/setup/chapters/Stylist.css';
import './SeSharing.css';

/**
 * Settings › Sharing for a self-employed stylist: everything that is theirs
 * to choose at this salon, one tab at a time (Oct 2026; this replaced
 * Settings › Your choices). Every switch already existed and saves through
 * the endpoint only the stylist can use, straight away, with a small "Saved".
 *
 *   bookings   who can see their diary, showing on the salon's booking page,
 *              and whose diaries they can see
 *   services   whose services they use (their shareService), their menu
 *   clients    who can see their clients; the salon's clients, read-only
 *   marketing  salon-run marketing, the salon's loyalty scheme (when it runs
 *              one), the salon's discount codes (behind DISCOUNTS_VISIBLE)
 *
 * The privacy switches are the setup chapter's own (shared/PrivacyChoices),
 * so setup and Settings say the same words and save the same way.
 */

/** One instant-save choice: optimistic, last tap wins, back on failure. */
function useInstant(initial, save) {
  const alive = useAlive();
  const serial = useSerial();
  const [flashed, flash] = useFlash();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => { setValue(initial); }, [initial]);
  const pick = (v) => {
    if (v === value) return;
    const before = value;
    setValue(v);
    setError('');
    setSaving(true);
    serial('v', () => saveRef.current(v))
      .then(() => { if (alive.current) flash('v'); })
      .catch((e) => {
        if (!alive.current) return;
        setValue(before);
        setError(reasonOf(e, 'That didn’t save. Try again.'));
        hapticError();
      })
      .finally(() => { if (alive.current) setSaving(false); });
  };
  return { value, saving, saved: !!flashed.v, error, pick };
}

function Status({ state }) {
  return (
    <>
      <div className="su-stylist-summary" aria-live="polite">
        {state.saving ? <span className="su-dots" aria-label="Saving"><i /><i /><i /></span> : state.saved ? <Saved /> : null}
      </div>
      {state.error && <div className="su-error" role="alert">{state.error}</div>}
    </>
  );
}

function Choice({ title, sub, children }) {
  return (
    <div className="yc-choice">
      <strong>{title}</strong>
      {sub && <small>{sub}</small>}
      {children && <div className="yc-choice-body">{children}</div>}
    </div>
  );
}

const Loading = () => <Skeleton h={120} style={{ borderRadius: 16 }} />;

const PRICE_WORDS = {
  salon: { chip: 'Salon menu, salon prices', line: (s) => `${s} uses one menu, with the same prices for everyone.` },
  salonOwnPrices: { chip: 'Salon menu, my prices', line: (s) => `${s} uses one menu. You set your own prices for it.` },
  own: { chip: 'My own menu', line: () => 'You run your own menu and set your own prices.' },
};

function marketingSave(me) {
  const m = me.choices.marketing;
  return async (v) => {
    const res = await marketingService.updateMarketingSetting({
      hairdresserId: me.userId,
      // Their own answer only; the salon's is sent back as it stands.
      salonConsent: m.offered ? 1 : 0,
      selfEmpConsent: v,
      controlledSettingId: m.controlledSettingId,
    });
    const d = res && res.data;
    if (!d || d.statusCode !== 1) {
      const e = new Error((d && d.error && (d.error.responseMessage || d.error.message)) || 'That didn’t save. Try again.');
      e.plain = true;
      throw e;
    }
  };
}

async function readShareService() {
  const res = await newServices.listServices({});
  const rd = res && res.data && res.data.responseData;
  if (!rd || rd.shareService == null) return null;
  return Number(rd.shareService) === 1 ? 1 : 0;
}

// The services endpoint can say OK either way, so it's read back before it counts.
async function saveShareService(v) {
  const res = await newServices.serviceShareSetting({ shareService: v });
  if (!res || !res.data || res.data.statusCode !== 1) throw new Error('refused');
  if ((await readShareService()) !== v) throw new Error('not saved');
}

export default function SeSharing({ tab, user }) {
  const ids = useMemo(() => myIds(user), [user]);
  const [meP] = useState(() => seChoicesService.mine());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [owner, setOwner] = useState({ first: '', id: null });
  const [shareService, setShareService] = useState(undefined);
  // Discount codes: their answer on the salon's codes, and their split (for
  // the worked example; sharing means nothing when the salon takes 0%)
  const [codesAnswer, setCodesAnswer] = useState(undefined);
  const [myCut, setMyCut] = useState(null);
  // The salon's booking setting + how many of its clients are on my list.
  const [clientShareInfo, setClientShareInfo] = useState(null);

  useEffect(() => {
    let off = false;
    meP.then((d) => { if (!off) setData(d); }).catch((e) => { if (!off) setError(reasonOf(e, 'I couldn’t load your choices just now.')); });
    loadTeam().then((rows) => {
      const o = ownerOf(rows);
      if (!off && o && !ids.includes(String(o._id))) setOwner({ first: firstWord(o.name), id: String(o._id) });
      const mine = (rows || []).find((r) => ids.includes(String(r._id)));
      if (!off && mine && mine.hairdresserInfo) setMyCut(Number(mine.hairdresserInfo.salonCommissionRate) || 0);
    }).catch(() => { /* the salon's name instead */ });
    clientSharingService.getSetting().then((r) => { if (!off) setClientShareInfo(r); }).catch(() => { /* the plain access line instead */ });
    readShareService().then((v) => { if (!off) setShareService(v); }).catch(() => { if (!off) setShareService(null); });
    if (DISCOUNTS_VISIBLE) {
      loadChoices().then((all) => {
        if (off) return;
        const mineId = ids.find((id) => all[String(id)]);
        setCodesAnswer(mineId ? answerOf(all[String(mineId)]) : null);
      }).catch(() => { if (!off) setCodesAnswer(null); });
    }
    return () => { off = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const share = useClientShare({
    ids, user,
    load: () => meP.then((d) => ({ clientShare: d.me.choices.clients.share, salonClientAccess: d.me.choices.salonClientAccess, cached: false })),
  });
  const diaryChoice = useDiaryChoice({ ids });

  const me = data && data.me;
  const c = me ? me.choices : null;
  const marketing = useInstant(c ? (c.marketing.accepted ? 1 : 0) : null, me ? marketingSave(me) : () => Promise.resolve());
  const loyalty = useInstant(c && c.loyalty ? (c.loyalty.joined ? 1 : 0) : null, (v) => marketingBackend.loyaltyJoin(v === 1).catch((e) => { e.plain = true; throw e; }));
  const booking = useInstant(c ? (c.bookingPage.shown ? 1 : 0) : null, (v) => saveProFlags({ showOnSalonProfile: v === 1 }));
  const codes = useInstant(codesAnswer === undefined ? null : codesAnswer, (v) => saveMyChoice(v));
  const services = useInstant(shareService === undefined ? null : shareService, saveShareService);

  const salonName = (data && data.salon && data.salon.name) || '';
  const salon = salonName || 'the salon';
  const Salon = salonName || 'The salon';
  // A self-employed login's team list is usually just them, so the owner's
  // name and diary row come from the diary-sharing list when Team has neither.
  const ownerRow = ((diaryChoice.diary && diaryChoice.diary.staff) || []).find((s) => s && s.isSalonOwner);
  const ownerFirst = owner.first || (ownerRow ? firstWord(ownerRow.name) : '');
  const ownerName = ownerFirst || 'The owner';
  const ownerId = ownerRow ? String(ownerRow.hairdresserId) : owner.id;

  if (error && !data) {
    return <div className="settings-card"><p className="yc-muted">{error}</p></div>;
  }

  if (tab === 'services') {
    const prices = data && PRICE_WORDS[data.salon.menuModel];
    return (
      <div className="yc-page">
        <section className="settings-card yc-card">
          <Choice title="Whose services do you use?" sub="Either way, you set your own price and time for each service you offer.">
            {shareService === undefined ? <Loading /> : (
              <>
                <Choices
                  columns={1}
                  label="Whose services do you use"
                  value={services.value}
                  onChange={services.pick}
                  options={[
                    { value: 1, label: `${Salon}’s services`, hint: 'The salon’s services and categories' },
                    { value: 0, label: 'My own menu', hint: 'Services and categories just for you' },
                  ]}
                />
                <Status state={services} />
              </>
            )}
          </Choice>
          <Choice title="My services and prices" sub={prices ? prices.line(Salon) : 'Your services and prices are on your menu.'}>
            {prices && <span className="yc-chip yc-chip--blue">{prices.chip}</span>}
            <Link className="yc-link" to="/new-services">Go to my services</Link>
          </Choice>
        </section>
      </div>
    );
  }

  if (tab === 'clients') {
    const access = share.clients ? share.clients.salonClientAccess : (c ? c.salonClientAccess : null);
    const onList = clientShareInfo ? Number(clientShareInfo.sharedWithMe) || 0 : 0;
    const bookAdds = !!(clientShareInfo && clientShareInfo.shareSEBookedClients !== false);
    const whose = ownerFirst ? `${ownerFirst}’s` : `${salon}’s`;
    // Not all of the salon's clients — but the ones on my list are mine to see.
    let salonLine = access === null || access === undefined ? `Set by ${salon}.` : salonAccessLine(access, { salon, Salon, ownerFirst });
    let chip = access === 1 ? 'All shared with you' : 'Not shared with you';
    if (access === 0 && clientShareInfo) {
      if (onList > 0) {
        salonLine = `${onList === 1 ? `1 of ${salon}’s clients is` : `${onList} of ${salon}’s clients are`} on your list${bookAdds ? `, and ${salon} adds the ones it books with you` : ''}. You don’t see the rest. That’s ${whose} choice.`;
        chip = `${onList} on your list`;
      } else if (bookAdds) {
        salonLine = `When ${salon} books you with one of its clients, they go on your list. You don’t see the rest. That’s ${whose} choice.`;
        chip = 'Added when booked';
      }
    }
    return (
      <div className="yc-page">
        <section className="settings-card yc-card">
          <Choice title="Who can see your clients?" sub={`Your clients are yours. Sharing them lets ${salon} help with bookings when you’re away.`}>
            <ClientShareChoice share={share} salon={salon} salonTitle={Salon} ownerFirst={ownerFirst} showAccess={false} />
          </Choice>
          <Choice title={`${Salon}’s clients`} sub={salonLine}>
            <span className={`yc-chip ${access === 1 || onList > 0 ? 'yc-chip--ok' : 'yc-chip--grey'}`}>{chip}</span>
          </Choice>
          <Choice title={`If you leave ${salon}`} sub={`You take your own clients with you, and a copy of ${salon}’s clients on your list. ${Salon} keeps its own clients.`} />
        </section>
      </div>
    );
  }

  if (tab === 'marketing') {
    return (
      <div className="yc-page">
        <section className="settings-card yc-card">
          {!c ? <Loading /> : (
            <>
              <Choice title="Who markets to your clients?" sub={`Your clients only get ${salon}’s emails and texts if you say so.`}>
                {c.marketing.controlledSettingId ? (
                  <>
                    <Choices
                      columns={1}
                      label="Who markets to your clients"
                      value={marketing.value}
                      onChange={marketing.pick}
                      options={[
                        { value: 1, label: `${Salon} does`, hint: 'Your clients get the salon’s emails and texts' },
                        { value: 0, label: 'I do', hint: 'You send your own from Marketing' },
                      ]}
                    />
                    {!c.marketing.offered && marketing.value === 1 && (
                      <p className="yc-note yc-choices-note">{Salon} hasn’t switched this on yet, so for now you still market to your own clients.</p>
                    )}
                    <Status state={marketing} />
                  </>
                ) : (
                  <p className="yc-note">{Salon} hasn’t offered to run your marketing, so you market to your own clients.</p>
                )}
              </Choice>

              {c.loyalty && (
                <Choice title={`${Salon}’s loyalty scheme`} sub={`${data.salon.loyaltyScheme && data.salon.loyaltyScheme.name ? `${data.salon.loyaltyScheme.name}. ` : ''}Join it and your clients earn points when they book with you too.`}>
                  {c.loyalty.offered ? (
                    <>
                      <Choices
                        compact
                        label="Loyalty"
                        value={loyalty.value}
                        onChange={loyalty.pick}
                        options={[
                          { value: 1, label: 'Join' },
                          { value: 0, label: 'Not for me' },
                        ]}
                      />
                      <Status state={loyalty} />
                    </>
                  ) : (
                    <p className="yc-note">{Salon} hasn’t offered its scheme to you yet. When it does, you can join here.</p>
                  )}
                </Choice>
              )}

              {DISCOUNTS_VISIBLE && (
                <Choice title={`${Salon}’s discount codes`} sub={`Can ${salon}’s codes be used on your services? Your own codes are always yours.`}>
                  {codesAnswer === undefined ? <Loading /> : (
                    <>
                      <Choices
                        compact
                        columns={1}
                        label={`${Salon}’s discount codes`}
                        value={codes.value}
                        onChange={codes.pick}
                        options={[
                          { value: 'salon', label: 'Only when the business owner pays', hint: 'You always get your full share. The business owner covers the discount.' },
                          ...(myCut === 0 ? [] : [{
                            value: 'shared',
                            label: 'Yes, and we can share the cost',
                            hint: (() => {
                              const cut = (myCut == null ? 40 : myCut) / 100;
                              const shared = splitFor({ price: 100, discount: 20, salonCut: cut, mode: 'shared' });
                              const full = splitFor({ price: 100, discount: 20, salonCut: cut, mode: 'salon' });
                              return `In line with your split. On a £100 service with a £20 code, you’d get ${money(shared.stylist)} instead of ${money(full.stylist)}.`;
                            })(),
                          }]),
                          { value: 'no', label: 'No', hint: 'Their codes won’t work on your services.' },
                        ]}
                      />
                      {codes.value === null && <p className="yc-note yc-choices-note">Until you choose, {salon}’s codes don’t work on your services.</p>}
                      {myCut === 0 && <p className="yc-note yc-choices-note">{Salon} doesn’t take a share of your takings, so there’s nothing to share. The business owner pays for any code they run on your services.</p>}
                      <Status state={codes} />
                    </>
                  )}
                </Choice>
              )}
            </>
          )}
        </section>
      </div>
    );
  }

  // bookings
  const others = ((diaryChoice.diary && diaryChoice.diary.staff) || [])
    .filter((s) => s && !ids.includes(String(s.hairdresserId)) && !ids.includes(String(s.userId)));
  return (
    <div className="yc-page">
      <section className="settings-card yc-card">
        <Choice title="Who can see your diary?" sub={`Whoever can see it can also book clients in with you. ${ownerName} can always see it. The front desk follows this too.`}>
          <DiaryChoice
            choice={diaryChoice}
            ids={ids}
            ownerId={ownerId}
            salon={salon}
            ownerName={ownerName}
            onRetry={() => { hapticTick(); diaryChoice.retry(); }}
            missingNote="Your diary settings aren’t ready here yet. Try again in a moment."
          />
        </Choice>
        <Choice title={`Show me on ${salon}’s booking page`} sub="Clients can pick you when they book online with the salon. Your own booking link works either way.">
          {!c ? <Loading /> : (
            <>
              <Choices
                compact
                label={`Show me on ${salon}’s booking page`}
                value={booking.value}
                onChange={booking.pick}
                options={[
                  { value: 1, label: 'Show me' },
                  { value: 0, label: 'Hide me' },
                ]}
              />
              <Status state={booking} />
            </>
          )}
        </Choice>
      </section>

      {others.length > 0 && (
        <section className="settings-card yc-card">
          <h2 className="yc-glabel">Diaries you can see</h2>
          {diaryChoice.diary && !diaryChoice.diary.bulkShare && (
            <p className="yc-note">{ownerName === 'The owner' ? 'The owner hasn’t' : `${ownerName} hasn’t`} switched on team diaries yet, so for now you only see your own. This is who would share with you when it’s on.</p>
          )}
          <ul className="shs-people">
            {others.map((s) => (
              <li key={s.hairdresserId}>
                <span className="shs-av" aria-hidden="true">{String(s.name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}</span>
                <span className="shs-name">{s.name || 'Team member'}</span>
                <span className={`shs-state${s.sharesWithCurrentUser ? ' shs-state--on' : ''}`}>{s.sharesWithCurrentUser ? 'Shares with you' : 'Keeps it private'}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
